import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Plus, Loader2, ClipboardList, FileDown, ScrollText, FileText, CreditCard } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { SERVICE_TYPES, REQUEST_TYPES, labelOf } from "../../lib/gmaoConstants";
import { QUOTE_STATUS_META } from "../../lib/quoteConstants";
import { INVOICE_STATUS_META, fmtEUR } from "../../lib/invoiceConstants";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import StatusBadge from "./StatusBadge";
import InterventionForm from "./InterventionForm";
import ClientInterventionDetail from "./ClientInterventionDetail";

const fmtDate = (iso) => {
  try { return format(new Date(iso), "dd/MM/yyyy", { locale: fr }); } catch { return "-"; }
};

const UNPAID = ["envoyee", "transmise_plateforme", "acceptee"];

const RequesterDashboard = ({ title, subtitle, showTenant, role }) => {
  const navigate = useNavigate();
  const [interventions, setInterventions] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [payingSel, setPayingSel] = useState({});
  const [payLoading, setPayLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, qRes, fRes] = await Promise.all([
        API.get("/interventions", { params: { limit: 200 } }),
        API.get("/quotes", { params: { limit: 200 } }),
        API.get("/invoices", { params: { limit: 200 } }),
      ]);
      setInterventions(iRes.data.interventions);
      setQuotes(qRes.data.quotes);
      setInvoices(fRes.data.invoices);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const quoteFor = (iid) => quotes.find((q) => q.interventionId === iid);
  const invoiceFor = (iid) => invoices.find((f) => f.interventionId === iid);

  const dl = async (url) => {
    try {
      const res = await API.get(url, { responseType: "blob" });
      const u = window.URL.createObjectURL(res.data);
      window.open(u, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(u), 10000);
    } catch { toast.error("Téléchargement impossible."); }
  };

  const openDetail = (i) => { setSelected(i); setDetailOpen(true); };

  const unpaidInvoices = invoices.filter((f) => UNPAID.includes(f.status));
  const selectedIds = Object.keys(payingSel).filter((id) => payingSel[id]);
  const selTotal = unpaidInvoices.filter((f) => payingSel[f.id]).reduce((s, f) => s + (f.totalTTC || 0), 0);

  const paySelection = async () => {
    if (selectedIds.length === 0) { toast.error("Sélectionnez au moins une facture."); return; }
    setPayLoading(true);
    try {
      const { data } = await API.post("/grouped-payments", { invoiceIds: selectedIds });
      navigate(`/paiement-groupe/${data.publicToken}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div data-testid="requester-dashboard">
      <Helmet><title>Mon espace – NGL SERVICES</title></Helmet>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <Button className="btn-primary" onClick={() => setFormOpen(true)} data-testid="new-request-button">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle demande
        </Button>
      </div>

      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(207,78%,42%)]" /></div>
      ) : (
        <Tabs defaultValue="demandes">
          <TabsList>
            <TabsTrigger value="demandes" data-testid="tab-demandes"><ClipboardList className="w-4 h-4 mr-2" /> Mes demandes</TabsTrigger>
            <TabsTrigger value="devis" data-testid="tab-devis"><ScrollText className="w-4 h-4 mr-2" /> Devis</TabsTrigger>
            <TabsTrigger value="factures" data-testid="tab-factures"><FileText className="w-4 h-4 mr-2" /> Factures</TabsTrigger>
          </TabsList>

          {/* Demandes */}
          <TabsContent value="demandes" className="mt-4">
            {interventions.length === 0 ? (
              <Card className="p-12 text-center">
                <ClipboardList className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground mb-4">Vous n'avez encore aucune demande.</p>
                <Button className="btn-primary" onClick={() => setFormOpen(true)}><Plus className="w-4 h-4 mr-2" /> Créer ma première demande</Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {interventions.map((i) => {
                  const q = quoteFor(i.id);
                  const f = invoiceFor(i.id);
                  return (
                    <Card key={i.id} className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDetail(i)} data-testid={`request-card-${i.dossierNumber}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm font-semibold text-[hsl(207,78%,42%)]">{i.dossierNumber}</span>
                        <StatusBadge status={i.status} />
                      </div>
                      <p className="text-sm font-medium text-slate-800">{labelOf(REQUEST_TYPES, i.requestType)} · {labelOf(SERVICE_TYPES, i.serviceType)}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{i.description}</p>
                      <div className="flex items-center gap-2 mt-3 flex-wrap text-[10px]">
                        {q && <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">Devis {q.number || ""}</span>}
                        {f && <span className="px-2 py-0.5 rounded bg-teal-50 text-teal-700">Facture {f.number || ""}</span>}
                        {(i.report || i.signature) && <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">Bon d'intervention</span>}
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                        <span>{i.city || i.address}</span>
                        <span>{fmtDate(i.createdAt)}</span>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Devis */}
          <TabsContent value="devis" className="mt-4">
            {quotes.length === 0 ? (
              <p className="p-10 text-center text-muted-foreground">Aucun devis pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {quotes.map((q) => (
                  <Card key={q.id} className="p-3 flex items-center justify-between" data-testid={`quote-doc-${q.id}`}>
                    <div>
                      <p className="font-mono text-sm font-semibold text-[hsl(207,78%,42%)]">{q.number || "Devis"}</p>
                      <span className={`text-[11px] px-2 py-0.5 rounded border ${QUOTE_STATUS_META[q.status]?.color || ""}`}>{QUOTE_STATUS_META[q.status]?.label || q.status}</span>
                      <span className="text-xs text-muted-foreground ml-2">{fmtEUR(q.totalTTC)}</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => dl(`/quotes/${q.id}/pdf`)} data-testid={`dl-quote-${q.id}`}>
                      <FileDown className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Factures */}
          <TabsContent value="factures" className="mt-4">
            {invoices.length === 0 ? (
              <p className="p-10 text-center text-muted-foreground">Aucune facture pour le moment.</p>
            ) : (
              <>
                {unpaidInvoices.length > 0 && (
                  <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3 mb-3 flex-wrap gap-2" data-testid="pay-selection-bar">
                    <span className="text-sm">{selectedIds.length} facture(s) sélectionnée(s) · <strong className="text-[hsl(207,78%,42%)]">{fmtEUR(selTotal)}</strong></span>
                    <Button className="btn-primary" size="sm" disabled={selectedIds.length === 0 || payLoading} onClick={paySelection} data-testid="pay-selection-button">
                      {payLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                      Payer la sélection
                    </Button>
                  </div>
                )}
                <div className="space-y-2">
                  {invoices.map((f) => {
                    const payable = UNPAID.includes(f.status);
                    return (
                      <Card key={f.id} className="p-3 flex items-center justify-between gap-2" data-testid={`invoice-doc-${f.id}`}>
                        <div className="flex items-center gap-3">
                          {payable && (
                            <Checkbox
                              checked={!!payingSel[f.id]}
                              onCheckedChange={(v) => setPayingSel((s) => ({ ...s, [f.id]: !!v }))}
                              data-testid={`pay-check-${f.id}`}
                            />
                          )}
                          <div>
                            <p className="font-mono text-sm font-semibold text-[hsl(207,78%,42%)]">{f.number || "Facture"}</p>
                            <span className={`text-[11px] px-2 py-0.5 rounded border ${INVOICE_STATUS_META[f.status]?.color || ""}`}>{INVOICE_STATUS_META[f.status]?.label || f.status}</span>
                            <span className="text-xs text-muted-foreground ml-2">{fmtEUR(f.totalTTC)}</span>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => dl(`/invoices/${f.id}/pdf`)} data-testid={`dl-invoice-${f.id}`}>
                          <FileDown className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      )}

      <InterventionForm open={formOpen} onOpenChange={setFormOpen} onCreated={() => load()} showTenant={showTenant} />

      <ClientInterventionDetail
        open={detailOpen}
        onOpenChange={setDetailOpen}
        intervention={selected}
        quote={selected ? quoteFor(selected.id) : null}
        invoice={selected ? invoiceFor(selected.id) : null}
        onAfterDownload={load}
      />
    </div>
  );
};

export default RequesterDashboard;
